import { nextTick, onBeforeUnmount, onMounted } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

export function useChatListKeyboardEvents(listRef) {
  const getConversationItems = () =>
    listRef.value?.querySelectorAll('[data-a11y-conversation-item]') || [];

  const getActiveIndex = items => {
    const focusedIndex = [...items].findIndex(
      item => item === document.activeElement
    );
    if (focusedIndex >= 0) return focusedIndex;

    const activeIndex = [...items].findIndex(
      item => item.getAttribute('aria-current') === 'true'
    );
    return activeIndex >= 0 ? activeIndex : 0;
  };

  const announce = message => {
    window.dispatchEvent(
      new CustomEvent('a11y-announce', { detail: { message } })
    );
  };

  const focusConversation = (
    index,
    { open = false, announceItem = false } = {}
  ) => {
    const items = getConversationItems();
    if (!items.length) return;

    const boundedIndex = Math.min(Math.max(index, 0), items.length - 1);
    const item = items[boundedIndex];
    item.focus({ preventScroll: true });
    item.scrollIntoView?.({ block: 'nearest' });

    if (announceItem) {
      const description = item.getAttribute('aria-label') || item.textContent;
      announce(
        `Conversa ${boundedIndex + 1} de ${items.length}. ${description}`
      );
    }

    if (open) {
      item.click();
    }
  };

  const handleListKeydown = event => {
    const items = getConversationItems();
    if (!items.length) return;

    const activeIndex = getActiveIndex(items);
    const actions = {
      ArrowDown: () => focusConversation(activeIndex + 1),
      ArrowUp: () => focusConversation(activeIndex - 1),
      Home: () => focusConversation(0),
      End: () => focusConversation(items.length - 1),
      Enter: () => focusConversation(activeIndex, { open: true }),
    };

    const action = actions[event.key];
    if (!action) return;

    event.preventDefault();
    action();
  };

  const focusCurrentConversation = async () => {
    await nextTick();
    const items = getConversationItems();
    if (!items.length) return;
    focusConversation(getActiveIndex(items));
  };

  const keyboardEvents = {
    ...Object.fromEntries(
      Array.from({ length: 9 }, (_, index) => [
        `Alt+Digit${index + 1}`,
        {
          action: event => {
            event.preventDefault();
            focusConversation(index, { open: true, announceItem: true });
          },
          allowOnFocusedInput: false,
        },
      ])
    ),
    'Alt+Digit0': {
      action: event => {
        event.preventDefault();
        const items = getConversationItems();
        if (!items.length) {
          announce('Nenhuma conversa disponível.');
          return;
        }
        focusConversation(getActiveIndex(items), { announceItem: true });
      },
      allowOnFocusedInput: false,
    },
  };

  useKeyboardEvents(keyboardEvents);

  onMounted(() => {
    listRef.value?.addEventListener('keydown', handleListKeydown);
    listRef.value?.addEventListener('focus', focusCurrentConversation);
  });

  onBeforeUnmount(() => {
    listRef.value?.removeEventListener('keydown', handleListKeydown);
    listRef.value?.removeEventListener('focus', focusCurrentConversation);
  });
}
